import { ErrorMessage, Field, Form, Formik } from "formik";
import Cookies from "js-cookie";
import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import * as Yup from "yup";
import OtpVerification from "./OtpVerification";
import LoginOtpScreen from "./LoginOtpScreen";
import ResetPasswordPage from "./ResetPasswordPage";
import AppContext from "../../context/AppContext";
import { generateToken } from "../../Push_notification/Notification";
import {
  getUserPermissions,
  loginUser,
  resendLoginOtp,
  storeFcmToken,
  verifyLoginOtp,
} from "../../service/AuthService";
import { RiLoader3Fill } from "react-icons/ri";
import LangComp from "../../components/_app/langComp";
import translations from "../../components/common/Translations";
import { getDefaultRouteFromPermissions } from "../../utils/permissions";
import {
  captureClientLocation,
  getClientIdentity,
  initClientIdentity,
} from "../../utils/clientIdentity";

const registrationSchema = (langMode) =>
  Yup.object().shape({
    email: Yup.string()
      .trim()
      .required(
        translations[langMode].UsernameRequired || "Username is required",
      ),
    password: Yup.string()
      .trim()
      .min(2, translations[langMode].PasswordMustBeAtLeast8Characters)
      .required(translations[langMode].PasswordRequired),
  });

const languageData = [
  { id: 1, lang: "en", langText: "English" },
  { id: 2, lang: "ur", langText: "Urdu" },
  { id: 3, lang: "ar", langText: "Arabic" },
];

const heroSlides = [
  {
    image: "/assets/slide1.jpg",
    titleKey: "loginHeroSlide1Title",
    descriptionKey: "loginHeroSlide1Description",
    stats: [
      { value: "1000+", labelKey: "loginHeroSlide1Stat1Label" },
      { value: "95%", labelKey: "loginHeroSlide1Stat2Label" },
    ],
  },
  {
    image: "/assets/slide2.jpg",
    titleKey: "loginHeroSlide2Title",
    descriptionKey: "loginHeroSlide2Description",
    stats: [
      { value: "100%", labelKey: "loginHeroSlide2Stat1Label" },
      { value: "Zero", labelKey: "loginHeroSlide2Stat2Label" },
    ],
  },
  {
    image: "/assets/slide3.jpg",
    titleKey: "loginHeroSlide3Title",
    descriptionKey: "loginHeroSlide3Description",
    stats: [
      { value: "98%", labelKey: "loginHeroSlide3Stat1Label" },
      { value: "150+", labelKey: "loginHeroSlide3Stat2Label" },
    ],
  },
  {
    image: "/assets/slide4.jpg",
    titleKey: "loginHeroSlide4Title",
    descriptionKey: "loginHeroSlide4Description",
    stats: [
      { value: "500+", labelKey: "loginHeroSlide4Stat1Label" },
      { value: "24/7", labelKey: "loginHeroSlide4Stat2Label" },
    ],
  },
];

const LoginPage = () => {
  const {
    setUser,
    fetchNotifications,
    toggleLangMode,
    langMode,
    setSelectedOrgId,
    theme,
  } = useContext(AppContext);

  const authInputWrapperClassName = `relative rounded-2xl transition-all duration-200 ${
    theme === "high-contrast"
      ? "border-2 border-black bg-white"
      : theme === "dark"
        ? "border border-gray-700 bg-slate-800/60 hover:border-[#236DB4]"
        : "border border-gray-200 bg-transparent hover:border-[#236DB4] hover:shadow-[0_0_0_3px_rgba(35,109,180,0.22)] focus-within:border-[#236DB4] focus-within:shadow-[0_0_0_3px_rgba(35,109,180,0.22)]"
  }`;

  const authInputClassName = `w-full min-w-0 rounded-2xl border-0 bg-transparent py-3 px-4 text-base outline-none ring-0 focus:outline-none focus:ring-0 ${
    theme === "high-contrast"
      ? "text-black placeholder:text-gray-600 font-medium"
      : theme === "dark"
        ? "text-gray-100 placeholder:opacity-50 placeholder:text-gray-400 placeholder:font-light"
        : "text-gray-700 placeholder:opacity-50 placeholder:text-gray-400 placeholder:font-light"
  }`;
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [pendingOtpEmail, setPendingOtpEmail] = useState("");
  const [otpPhoneNumber, setOtpPhoneNumber] = useState("");
  const [rememberLogin, setRememberLogin] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [isOtpResending, setIsOtpResending] = useState(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const navbarRef = useRef(null);
  const formikRef = useRef(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const isRtl = langMode === "ar" || langMode === "ur";
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const getResponseMessage = (source, fallback) => {
    const responseData = source?.response?.data || source;
    const bodyValue = responseData?.body?.value;

    return (
      responseData?.body?.error ||
      (typeof bodyValue === "string" ? bodyValue : null) ||
      responseData?.message ||
      source?.message ||
      source?.body?.error ||
      (typeof source?.body?.value === "string" ? source.body.value : null) ||
      fallback
    );
  };

  const completeLogin = async (
    { accessToken, userDetails },
    shouldRemember = rememberLogin,
  ) => {
    setUser(userDetails);
    sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
    localStorage.setItem("userDetails", JSON.stringify(userDetails));

    if (userDetails?.organization?.organizationId) {
      localStorage.setItem(
        "selectedOrgId",
        userDetails.organization.organizationId,
      );
      setSelectedOrgId(userDetails.organization.organizationId);
    }

    localStorage.setItem("justLoggedIn", "true");
    setLoading(false);
    if (shouldRemember) {
      window.localStorage.setItem("isLoggedIn", true);
    }
    localStorage.setItem("accessToken", accessToken);

    const expires = new Date();
    expires.setTime(expires.getTime() + 7 * 60 * 60 * 1000);
    Cookies.set("accessToken", accessToken, { expires });
    Cookies.set("userType", userDetails.userType, {
      secure: true,
      sameSite: "strict",
    });
    const permissionsResponse = await getUserPermissions(accessToken).catch(
      (err) => console.warn("User permissions fetch failed:", err),
    );
    if (
      permissionsResponse?.header?.code === 600 &&
      permissionsResponse?.body?.value
    ) {
      const permissionsValue = permissionsResponse.body.value;
      Cookies.set("userPermissions", JSON.stringify(permissionsValue), {
        expires,
      });
      localStorage.setItem(
        "userPermissions",
        JSON.stringify(permissionsValue),
      );
    }
    Cookies.set("userId", userDetails.userId);
    Cookies.set("userName", userDetails.name);
    Cookies.set("userVisibility", userDetails.userVisibility);
    Cookies.set("userEmail", userDetails.email, { httpOnly: true });
    Cookies.set("profilePic", userDetails.profilePic);
    Cookies.set("firstTimeLogin", userDetails.firstTimeLogin);
    const allowedPages = userDetails?.allowedPages || [];
    const defaultRoute =
      getDefaultRouteFromPermissions() || allowedPages[0] || "/maptask";
    navigate(defaultRoute);

    toast.success(
      `${
        userDetails.firstTimeLogin === "false"
          ? translations[langMode].welcomeback
          : translations[langMode].welcome
      } ${userDetails.name}`,
    );
    const token = await generateToken();
    await storeFcmToken(token, accessToken);
    fetchNotifications(0);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const trimmedValues = {
        email: (values.email || "").trim(),
        password: (values.password || "").trim(),
      };
      await initClientIdentity();
      await captureClientLocation();
      const { systemId, ipAddress, latitude, longitude } = getClientIdentity();
      console.log("System UUID:", systemId);
      console.log("IP Address:", ipAddress || "Not available");
      console.log("Latitude:", latitude ?? "Not available");
      console.log("Longitude:", longitude ?? "Not available");
      const responseData = await loginUser(trimmedValues);
      const rememberCheckbox = document.getElementById("remember");
      const isChecked = rememberCheckbox?.checked;
      setRememberLogin(isChecked);

      if (responseData.header.code === 600) {
        await completeLogin(responseData.body.value, isChecked);
      } else if (responseData.header.code === 612) {
        toast.error(translations[langMode].emailidorpass);
        setLoading(false);
      } else if (responseData.header.code === 613) {
        if (responseData.body?.value?.requiresOtp === true) {
          setPendingOtpEmail(responseData.body.value.email || trimmedValues.email);
          setOtpPhoneNumber(
            responseData.body.value.maskedPhone ||
              responseData.body.value.phoneNo ||
              "",
          );
          setOtpError("");
          setLoading(false);
        } else {
          setResetEmail(trimmedValues.email);
          setResetPassword(true);
        }
      } else if (responseData.header.code === 608) {
        toast.error(translations[langMode].accessdenied);
        setLoading(false);
      } else toast.error(responseData.body.value);
    } catch (error) {
      console.error("Error:", error);
      const backendMessage =
        error?.response?.data?.body?.value ??
        error?.response?.data?.message ??
        error?.body?.value;
      if (backendMessage) {
        toast.error(backendMessage);
      }
      setSubmitting(false);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleOtpVerify = async (otp) => {
    try {
      setIsOtpVerifying(true);
      setOtpError("");
      const responseData = await verifyLoginOtp(pendingOtpEmail, otp);
      if (responseData.header.code !== 600) {
        const message = getResponseMessage(responseData, "Unable to verify OTP.");
        setOtpError(message);
        toast.error(message);
        return;
      }

      setPendingOtpEmail("");
      await completeLogin(responseData.body.value);
    } catch (error) {
      const message = getResponseMessage(error, "Unable to verify OTP.");
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleOtpResend = async () => {
    try {
      setIsOtpResending(true);
      setOtpError("");
      const responseData = await resendLoginOtp(pendingOtpEmail);
      if (responseData.header.code !== 600) {
        throw new Error(getResponseMessage(responseData, "Unable to resend OTP."));
      }
      toast.success(
        getResponseMessage(responseData, "A new verification code was sent."),
      );
    } catch (error) {
      const message = getResponseMessage(error, "Unable to resend OTP.");
      setOtpError(message);
      toast.error(message);
      throw error;
    } finally {
      setIsOtpResending(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavbarOpen(false);
      }
    };

    if (isNavbarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavbarOpen]);

  const validationSchema = useMemo(() => {
    return registrationSchema(langMode);
  }, [langMode]);

  useEffect(() => {
    const formik = formikRef.current;
    if (!formik) return;
    if (Object.keys(formik.touched || {}).length === 0) return;
    formik.validateForm();
  }, [langMode]);

  // Right panel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // change slide every 5s
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index) => setActiveSlide(index);

  if (pendingOtpEmail) {
    return (
      <LoginOtpScreen
        phoneNumber={otpPhoneNumber}
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onBack={() => {
          setPendingOtpEmail("");
          setOtpPhoneNumber("");
          setOtpError("");
        }}
        isVerifying={isOtpVerifying}
        isResending={isOtpResending}
        error={otpError}
      />
    );
  }

  return (
    <>
      {!resetEmail && !resetPassword && (
        <div
          dir={isRtl ? "rtl" : "ltr"}
          className={`h-[100dvh] overflow-hidden flex flex-col lg:flex-row ${
            theme === "dark"
              ? "dark-mode bg-slate-900 text-slate-100"
              : theme === "high-contrast"
                ? "high-contrast bg-white text-black"
                : "bg-white text-gray-900"
          }`}
        >
          {/* MOBILE TOP BAR */}
          <div className="lg:hidden h-16 bg-gradient-to-r from-[#0B2E63] via-[#123a72] to-[#1a4a8f] px-3 sm:px-4 flex items-center justify-between shadow-md shadow-black/10 relative z-20 shrink-0">
            <img
              src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png"
              alt="Logo"
              className="h-7 object-contain brightness-0 invert shrink-0"
            />
            <LangComp
              isShowIcon
              variant="dark"
              languageDataList={languageData}
              toggleLangMode={toggleLangMode}
              isNavbarOpen={isNavbarOpen}
              menuPosition="absolute"
            />
          </div>

          {/* LEFT SIDE - LOGIN FORM */}
          <div className="w-full lg:w-[55%] flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            <div className="w-full px-4 xs:px-6 sm:px-8 md:px-10 lg:px-12 xl:px-16 flex flex-col flex-1 min-h-0">
              {/* HEADER ROW - logo left, language toggle right */}
              <div className="hidden lg:flex h-20 xl:h-24 items-center justify-between flex-shrink-0">
                <img
                  src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png"
                  alt="Logo"
                  className={`h-8 xl:h-9 object-contain shrink-0 ${
                    theme === "dark" ? "brightness-0 invert" : ""
                  }`}
                />
                <LangComp
                  isShowIcon
                  variant={theme === "dark" ? "dark" : "light"}
                  languageDataList={languageData}
                  toggleLangMode={toggleLangMode}
                  isNavbarOpen={isNavbarOpen}
                  menuPosition="absolute"
                />
              </div>

              <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
                <div
                  className={`w-full max-w-sm sm:max-w-md ${isRtl ? "text-right" : "text-left"}`}
                >
                  <h2
                    className={`text-2xl sm:text-[30px] lg:text-[34px] leading-tight font-bold mb-2 ${
                      theme === "dark" ? "text-white" : "text-[#030a1e]"
                    }`}
                  >
                    {translations[langMode]?.welcomeback}
                  </h2>
                  <p
                    className={`text-sm sm:text-[15px] font-light mb-6 sm:mb-8 ${
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {translations[langMode]?.loginEnterEmailPassword}
                  </p>

                  <Formik
                    innerRef={formikRef}
                    initialValues={{ email: "", password: "" }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Email */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1.5 ${
                              theme === "dark" ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {translations[langMode]?.UsernameEmailAddress}
                          </label>
                          <div className={authInputWrapperClassName}>
                            <Field
                              type="text"
                              name="email"
                              placeholder={
                                translations[langMode]?.loginEmailPlaceholder ||
                                "Enter your username or email"
                              }
                              className={`${authInputClassName} ${isRtl ? "text-right" : "text-left"}`}
                            />
                          </div>
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        {/* Password */}
                        <div>
                          <label
                            className={`block text-sm font-medium mb-1.5 ${
                              theme === "dark" ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {translations[langMode]?.password}
                          </label>
                          <div className={authInputWrapperClassName}>
                            <Field
                              type={showPassword ? "text" : "password"}
                              name="password"
                              placeholder={
                                translations[langMode]
                                  ?.loginPasswordPlaceholder ||
                                "Enter your password"
                              }
                              className={`${authInputClassName} ${isRtl ? "pl-12 text-right" : "pr-12 text-left"}`}
                            />
                            <button
                              type="button"
                              onClick={togglePasswordVisibility}
                              className={`absolute top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors touch-manipulation text-base outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${isRtl ? "left-1.5" : "right-1.5"}`}
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                            >
                              {showPassword ? (
                                <EyeOff className="w-5 h-5" />
                              ) : (
                                <Eye className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="text-red-500 text-xs mt-1"
                          />
                        </div>

                        {/* Remember + Forgot */}
                        <div className="flex justify-between items-center text-sm pt-1 select-none">
                          <label
                            className={`flex items-center gap-2 cursor-pointer ${
                              theme === "high-contrast"
                                ? "text-black font-semibold"
                                : "text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            <input
                              type="checkbox"
                              id="remember"
                              className={`w-4 h-4 rounded cursor-pointer shrink-0 ${
                                theme === "high-contrast"
                                  ? "border-2 border-black accent-black"
                                  : "border-gray-300 accent-[#236DB4]"
                              }`}
                            />
                            {translations[langMode]?.RememberMe ||
                              translations[langMode]?.remember ||
                              "Remember me"}
                          </label>
                          {/* <span className="text-[#236DB4] font-semibold cursor-pointer">
                            {translations[langMode]?.forgetpass ||
                              "Forget password"}
                          </span> */}
                        </div>

                        {/* Login Button */}
                        <div className="w-full pt-2">
                          <button data-button-intent="primary"
                            type="submit"
                            disabled={isSubmitting || loading}
                            className={`w-full min-w-0 !justify-center touch-manipulation text-base font-semibold rounded-xl py-3 min-h-[46px] transition flex items-center shadow-sm ${
                              theme === "high-contrast"
                                ? "bg-black text-white hover:bg-neutral-800 active:bg-neutral-900 border-2 border-black"
                                : "bg-[#1F5C8F] hover:bg-[#1a4d78] active:bg-[#164267] text-white"
                            }`}
                          >
                            {loading ? (
                              <RiLoader3Fill className="animate-spin text-xl mx-auto" />
                            ) : (
                              translations[langMode]?.Login?.trim() || "Sign in"
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>

                  <p
                    className={`text-center text-sm sm:text-[15px] font-light mt-4 mb-4 ${
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {translations[langMode]?.loginNoAccountText ||
                      "Don't have an account?"}{" "}
                    <span className="text-[#236DB4] font-semibold">
                      {translations[langMode]?.loginContactAdminText ||
                        "Contact your administrator"}
                    </span>
                  </p>

                  <div className="border-t border-gray-100 dark:border-gray-800 pt-4 mt-2 text-center pb-4 lg:pb-6">
                    <p className="text-[10px] lg:text-xs text-gray-400">
                      {`${(translations[langMode]?.loginPoweredByText || "Powered by Outbox")
                        .replace(/Outbox/gi, "")
                        .trimEnd()} `}
                      <a
                        href="https://www.outbox.om/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#236DB4] hover:underline"
                      >
                        OUTBOX
                      </a>
                    </p>
                    <p className="text-[10px] lg:text-xs text-gray-400 mt-0.5">
                      {translations[langMode]?.loginMadeWithLoveText ||
                        "Engineered in the Sultanate Of OMAN"}{" "}
                      <span className="text-rose-400">{"\u2764"}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - THEME PANEL / AUTO SLIDER */}
          <div className="hidden lg:flex lg:w-[45%] relative flex-col items-center justify-start px-6 xl:px-12 overflow-hidden flex-shrink-0 bg-[#236DB4]">
            {/* BACKGROUND IMAGES - crossfade stack */}
            {heroSlides.map((slide, index) => (
              <img
                key={index}
                src={slide.image}
                alt={translations[langMode]?.[slide.titleKey] || ""}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                  index === activeSlide ? "opacity-100" : "opacity-0"
                }`}
              />
            ))}

            {/* DARK OVERLAY */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B2E63]/70 via-[#0B2E63]/50 to-[#0B2E63]/80" />

            <div
              className={`relative z-10 flex-1 w-full max-w-[760px] flex flex-col justify-center ${isRtl ? "items-end" : "items-start"}`}
            >
              {/* TEXT CONTENT - crossfades with the image */}
              <div
                className={`relative w-full min-h-[170px] xl:min-h-[190px] ${isRtl ? "text-right" : "text-left"}`}
              >
                {heroSlides.map((slide, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                      index === activeSlide
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                  >
                    <h1
                      className={`text-2xl xl:text-4xl text-white font-semibold mb-3 xl:mb-4 ${isRtl ? "text-right" : "text-left"}`}
                    >
                      {translations[langMode]?.[slide.titleKey]}
                    </h1>
                    <p
                      className={`text-white/90 text-sm sm:text-base xl:text-xl leading-relaxed ${isRtl ? "text-right" : "text-left"}`}
                    >
                      {translations[langMode]?.[slide.descriptionKey]}
                    </p>
                  </div>
                ))}
              </div>

              {/* STAT CARDS - crossfade with slide */}
              <div
                className={`relative flex gap-3 xl:gap-4 w-full min-h-[100px] mt-2 ${isRtl ? "justify-end" : "justify-start"}`}
              >
                {heroSlides.map((slide, index) => (
                  <div
                    key={index}
                    className={`absolute ${isRtl ? "right-0 justify-end" : "left-0 justify-start"} flex gap-3 xl:gap-4 transition-opacity duration-1000 ease-in-out ${
                      index === activeSlide
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                  >
                    {slide.stats.map((stat, sIndex) => (
                      <div
                        key={sIndex}
                        className={`bg-[#0B2E63]/60 border border-white/10 rounded-2xl px-4 xl:px-6 py-3.5 xl:py-4 w-[180px] xl:w-[220px] max-w-full min-h-[90px] flex flex-col justify-center ${isRtl ? "text-right" : "text-left"}`}
                      >
                        <div className="text-white text-2xl xl:text-4xl leading-none font-extrabold">
                          {stat.value}
                        </div>
                        <div className="text-white/70 text-xs sm:text-sm xl:text-base mt-1.5 truncate">
                          {translations[langMode]?.[stat.labelKey]}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* DOT NAVIGATION - clickable */}
            <div className="relative z-10 mt-auto mb-6 flex gap-2 items-center">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === activeSlide
                      ? "w-6 bg-white"
                      : "w-2 bg-white/40 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {resetEmail && !resetPassword && <OtpVerification emaill={resetEmail} />}
      {resetPassword && <ResetPasswordPage emaill={resetEmail} />}
    </>
  );
};

export default LoginPage;
